import { createMcpServerRecord, createMcpServerStore } from "../persistence/mcpServerStore"
import { createPersonalSettingsStoreFromValue } from "../persistence/personalSettingsStore"
import { type AccessLevel, summarizeGrants } from "../computers/people"
import { makeAutoObservable, reaction, runInAction } from "mobx"
import { analytics, track } from "../analytics"
import { ACTIVITY_SUMMARY_KEY, ACTIVITY_SUMMARY_NAMESPACE, buildComputerActivitySummary } from "../computers/activitySummary"
import { DEFAULT_HARNESS_ID, DEFAULT_MODEL, getDefaultModelForHarness, isHarnessId, providerModelRegistry } from "../constants"
import { getDeviceConfig, setDeviceId as setDeviceConfigDeviceId, setTelemetryDisabled } from "../electronAPIs/deviceConfig"
import { type NestNotification, connectNestClient, nestClient, requestNestMethod } from "../nest/client"
import type { Turn } from "../nest/generated"
import type { McpServerStore } from "../persistence/mcpServerStore"
import { type McpServerStoreConnection, connectMcpServerStore } from "../persistence/mcpServerStoreBootstrap"
import { normalizeNewTaskPinnedAgents, type NewTaskAgentTarget } from "../persistence/newTaskAgentPins"
import { type PersonalSettingsAppDataConnection, connectPersonalSettingsAppDataStore } from "../persistence/personalSettingsAppData"
import type { PersonalSettings, PersonalSettingsStore } from "../persistence/personalSettingsStore"
import type { GitDiffStrategy } from "../utils/gitDiffStrategy"
import { NestProductStateStore, type ProductStateStore } from "../persistence/productState"
import { REPOS_NAMESPACE, TASKS_NAMESPACE, type TaskRecord, taskIdFromRecordKey } from "../persistence/records"
import { SavedActionStore } from "../persistence/savedActionStore"
import { TaskTimelineStore } from "../persistence/taskTimelineStore"
import { TaskProposalStore } from "../persistence/taskProposalStore"
import { recordMobileReconciliationSuccess } from "../platform/mobileDiagnostics"
import type { HarnessId } from "../providerEvents/harnessEventTypes"
import type { User } from "../types"
import type { ThinkingLevel } from "./TaskModel"
import { TaskOwnershipIndex } from "./TaskOwnershipIndex"

import { AccountUsageManager } from "./managers/AccountUsageManager"
import { CommentManager } from "./managers/CommentManager"
import { EventManager } from "./managers/EventManager"
import { ExecutionManager } from "./managers/ExecutionManager"
import { McpServerManager } from "./managers/McpServerManager"
import { NotificationManager } from "./managers/NotificationManager"
import { QueryManager } from "./managers/QueryManager"
import { QueuedTurnManager } from "./managers/QueuedTurnManager"
import { RepoManager } from "./managers/RepoManager"
import { type NestTurnRuntimeUpdate, RuntimeManager } from "./managers/RuntimeManager"
import { reconcileRuntimeState, turnRuntimeUpdateFromNestTurn } from "./managers/RuntimeReconciliation"
import { ScratchpadManager } from "./managers/ScratchpadManager"
import { SmartEditorManagerStore } from "./managers/SmartEditorManager"
import { type TaskCreation, TaskCreationManager, type TaskCreationOptions } from "./managers/TaskCreationManager"
import { TaskManager } from "./managers/TaskManager"
import { UIStateManager, type ViewMode } from "./managers/UIStateManager"

export type { TaskCreationOptions, TaskCreation, ViewMode }

export interface CodeStoreConfig {
    getCurrentUser: () => User
    navigateToTask: (workspaceId: string, taskId: string) => void
}

export type NodeAccess =
    | { state: "unknown" }
    | {
          state: "ready"
          node: boolean
          level: AccessLevel | "none"
          workspaceIds: string[]
          principalId: string
          kind: string
          displayName: string
          paused: boolean
          expiresAt?: string
          glyph?: string
          color?: string
      }

const NODE_ACCESS_UNKNOWN: NodeAccess = { state: "unknown" }

function guestPersonalSettingsConnection(): PersonalSettingsAppDataConnection {
    return { store: createPersonalSettingsStoreFromValue(undefined), sync: async () => undefined, disconnect: () => undefined }
}

function guestMcpServerConnection(): McpServerStoreConnection {
    return { store: createMcpServerStore(createMcpServerRecord()), sync: async () => undefined, disconnect: () => undefined }
}

const ANALYTICS_DEVICE_ID_BACKUP_KEY = "wildlark-analytics-device-id"
const PENDING_NEST_TURN_UPDATE_TTL_MS = 30_000
const PENDING_NEST_TURN_UPDATE_MAX = 100

type AnalyticsDeviceIdSource =
    | "device_config_existing"
    | "device_config_generated"
    | "personal_settings_backup"
    | "local_storage_backup"
    | "personal_settings"
    | "local_storage"
    | "generated"

interface NewTaskAgentDefaults {
    thinking: ThinkingLevel
    harnessId: HarnessId
    modelId: string
}

type TerminalActionStatus = "completed" | "error" | "stopped"

interface ParsedNestTurnUpdate extends NestTurnRuntimeUpdate {
    receivedAt: number
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringField(record: Record<string, unknown> | null, key: string): string | undefined {
    const value = record?.[key]
    return typeof value === "string" && value ? value : undefined
}

function boolField(record: Record<string, unknown> | null, key: string): boolean {
    return record?.[key] === true
}

function statusFromTurnNotification(record: Record<string, unknown>, turnRecord: Record<string, unknown> | null): string | undefined {
    const status = stringField(record, "status") ?? stringField(turnRecord, "status")
    if (status) return status
    if (boolField(record, "cancelled")) return "cancelled"
    if (boolField(record, "interrupted")) return "stopped"
    if (boolField(record, "completed")) return "completed"
    return undefined
}

function nestTerminalActionStatus(status: string): TerminalActionStatus | null {
    switch (status) {
        case "completed":
            return "completed"
        case "error":
        case "failed":
            return "error"
        case "stopped":
        case "cancelled":
            return "stopped"
        default:
            return null
    }
}

function nestTurnUpdateKey(turnId: string, sessionId?: string): string {
    return sessionId ? `${sessionId}:${turnId}` : turnId
}

function parseNestTurnUpdate(params: unknown): ParsedNestTurnUpdate | null {
    const record = asRecord(params)
    if (!record) return null
    const turnRecord = asRecord(record.turn)
    const sessionId = stringField(record, "sessionId") ?? stringField(turnRecord, "sessionId")
    const turnId = stringField(record, "turnId") ?? stringField(turnRecord, "turnId")
    const status = statusFromTurnNotification(record, turnRecord)
    if (!turnId || !status) return null

    return {
        turnId,
        ...(sessionId ? { sessionId } : {}),
        status,
        queuedAt: stringField(record, "queuedAt") ?? stringField(turnRecord, "queuedAt"),
        startedAt: stringField(record, "startedAt") ?? stringField(turnRecord, "startedAt"),
        completedAt: stringField(record, "completedAt") ?? stringField(turnRecord, "completedAt"),
        error: stringField(record, "error") ?? stringField(turnRecord, "error"),
        receivedAt: Date.now(),
    }
}

function isModelForHarness(harnessId: HarnessId, modelId: string | undefined): modelId is string {
    return providerModelRegistry.isModelForHarness(harnessId, modelId)
}

function getNewTaskAgentDefaults(settings: PersonalSettings | undefined): NewTaskAgentDefaults {
    const harnessId = isHarnessId(settings?.newTaskHarnessId) ? settings.newTaskHarnessId : DEFAULT_HARNESS_ID
    const modelId = isModelForHarness(harnessId, settings?.newTaskModelId) ? settings.newTaskModelId : getDefaultModelForHarness(harnessId)

    return { harnessId, modelId, thinking: settings?.newTaskThinking ?? "max" }
}

function getLocalAnalyticsDeviceIdBackup(): string | null {
    if (typeof window === "undefined") return null

    try {
        const storage = window.localStorage
        if (!storage) return null
        const value = storage.getItem(ANALYTICS_DEVICE_ID_BACKUP_KEY)?.trim()
        return value || null
    } catch (err) {
        console.warn("[Analytics] Failed to read local device ID backup:", err)
        return null
    }
}

function setLocalAnalyticsDeviceIdBackup(deviceId: string): void {
    if (typeof window === "undefined") return

    try {
        const storage = window.localStorage
        if (!storage) return
        storage.setItem(ANALYTICS_DEVICE_ID_BACKUP_KEY, deviceId)
    } catch (err) {
        console.warn("[Analytics] Failed to write local device ID backup:", err)
    }
}

export class CodeStore {
    readonly config: CodeStoreConfig
    defaultModel: string = DEFAULT_MODEL
    defaultThinking: ThinkingLevel = "max"
    defaultFastMode = false
    defaultHarnessId: HarnessId = DEFAULT_HARNESS_ID

    productState: ProductStateStore | null = null
    mcpServerStore: McpServerStore | null = null
    personalSettingsStore: PersonalSettingsStore | null = null
    savedActions: SavedActionStore | null = null
    private mcpServerStoreConnection: McpServerStoreConnection | null = null
    private personalSettingsStoreConnection: PersonalSettingsAppDataConnection | null = null
    private pendingNestTurnUpdates: Map<string, ParsedNestTurnUpdate> = new Map()
    private nestNotificationDisposer: (() => void) | null = null
    private nestStatusDisposer: (() => void) | null = null
    private productStateRecordDisposer: (() => void) | null = null
    private activitySummaryReactionDisposer: (() => void) | null = null
    private activitySummaryTaskRecords: Map<string, TaskRecord> = new Map()
    private readonly taskOwnership = new TaskOwnershipIndex()
    private activitySummaryWriteTimer: ReturnType<typeof setTimeout> | null = null
    private activitySummaryWriteQueue: Promise<void> = Promise.resolve()
    private providerListRefreshPromise: Promise<void> | null = null
    private runtimeReconciliationPromise: Promise<void> | null = null
    private runtimeRefreshQueue: Promise<void> = Promise.resolve()
    private telemetryReactionDisposer: (() => void) | null = null
    private pingIntervalId: ReturnType<typeof setInterval> | null = null
    private focusHandler: (() => void) | null = null
    private blurHandler: (() => void) | null = null
    storeInitialized = false
    storeInitializing = false
    private storeInitPromise: Promise<void> | null = null
    /**
     * What this client may do on the connected computer, read from its own
     * principal after every connect. A colleague's shared project connects
     * with workspace-scoped grants and renders in guest scope.
     */
    access: NodeAccess = NODE_ACCESS_UNKNOWN

    readonly ui: UIStateManager
    readonly queries: QueryManager
    readonly repos: RepoManager
    readonly tasks: TaskManager
    readonly events: EventManager
    readonly execution: ExecutionManager
    readonly accountUsage: AccountUsageManager
    readonly creation: TaskCreationManager
    readonly notifications: NotificationManager
    readonly comments: CommentManager
    readonly mcpServers: McpServerManager
    readonly smartEditors: SmartEditorManagerStore
    readonly runtimes: RuntimeManager
    readonly queuedTurns: QueuedTurnManager
    readonly scratchpads: ScratchpadManager
    readonly timelines: TaskTimelineStore
    readonly proposals: TaskProposalStore

    constructor(config: CodeStoreConfig) {
        this.config = config
        this.ui = new UIStateManager()
        this.queries = new QueryManager(this)
        this.repos = new RepoManager(this)
        this.events = new EventManager(this)
        this.execution = new ExecutionManager(this)
        this.accountUsage = new AccountUsageManager()
        this.tasks = new TaskManager(this)
        this.creation = new TaskCreationManager(this)
        this.notifications = new NotificationManager(this)
        this.comments = new CommentManager(this)
        this.mcpServers = new McpServerManager(this)
        this.smartEditors = new SmartEditorManagerStore()
        this.runtimes = new RuntimeManager()
        this.queuedTurns = new QueuedTurnManager()
        this.scratchpads = new ScratchpadManager()
        this.timelines = new TaskTimelineStore()
        this.proposals = new TaskProposalStore()

        makeAutoObservable<CodeStore, "taskOwnership">(this, {
            productState: true,
            mcpServerStore: true,
            personalSettingsStore: true,
            savedActions: true,
            storeInitialized: true,
            storeInitializing: true,
            access: true,
            ui: false,
            queries: false,
            repos: false,
            tasks: false,
            events: false,
            execution: false,
            accountUsage: false,
            creation: false,
            notifications: false,
            comments: false,
            mcpServers: false,
            smartEditors: false,
            runtimes: false,
            queuedTurns: false,
            taskOwnership: false,
            scratchpads: false,
            timelines: false,
            proposals: false,
        })
    }

    async initializeStores(): Promise<void> {
        if (this.storeInitialized) return

        if (this.storeInitializing && this.storeInitPromise) {
            return this.storeInitPromise
        }

        runInAction(() => {
            this.storeInitializing = true
        })

        this.storeInitPromise = this._doInitializeStores()
        return this.storeInitPromise
    }

    private async _doInitializeStores(): Promise<void> {
        try {
            await connectNestClient()
            await this.refreshAccess()
            await this.refreshProviderList()
            this.installNestReconnectRefresh()

            const productState = new NestProductStateStore()
            const savedActions = new SavedActionStore()
            // A guest on a colleague's computer cannot read that node's
            // personal settings, connectors, or saved Actions, so those live
            // in memory for the visit instead of failing the whole boot.
            const [mcpConnection, personalSettingsConnection] = this.isGuest
                ? [guestMcpServerConnection(), guestPersonalSettingsConnection()]
                : await Promise.all([connectMcpServerStore(), connectPersonalSettingsAppDataStore()])

            if (this.isGuest) savedActions.loadLocalDefaults()
            await Promise.all([
                productState.load(),
                this.isGuest ? Promise.resolve() : savedActions.load(),
                mcpConnection.sync(),
                personalSettingsConnection.sync(),
            ])

            const newTaskDefaults = getNewTaskAgentDefaults(personalSettingsConnection.store.settings.get())

            this.nestNotificationDisposer?.()
            const nestNotificationDisposer = await this.subscribeToNestNotifications()

            runInAction(() => {
                this.productState = productState
                this.mcpServerStoreConnection = mcpConnection
                this.mcpServerStore = mcpConnection.store
                this.personalSettingsStoreConnection = personalSettingsConnection
                this.personalSettingsStore = personalSettingsConnection.store
                this.savedActions = savedActions
                this.defaultHarnessId = newTaskDefaults.harnessId
                this.defaultModel = newTaskDefaults.modelId
                this.defaultThinking = newTaskDefaults.thinking
                this.storeInitialized = true
                this.storeInitializing = false
            })

            this.productStateRecordDisposer?.()
            this.taskOwnership.seed(productState.tasks.values())
            this.productStateRecordDisposer = productState.onRecordChanged((namespace, key) => {
                if (namespace === TASKS_NAMESPACE) {
                    const taskId = taskIdFromRecordKey(key)
                    if (!taskId) return
                    this.taskOwnership.reindex(taskId, this.productState?.tasks.get(taskId) ?? null)
                    this.tasks.scheduledTurns.reconcileTask(taskId)
                    this.updateActivitySummaryTaskRecord(taskId)
                    void this.applyPendingNestTurnUpdatesForTask(taskId).catch((error) => {
                        console.warn("[CodeStore] Failed to apply pending turn updates after task sync", { taskId, error })
                    })
                } else if (namespace === REPOS_NAMESPACE) {
                    this.scheduleComputerActivitySummaryWrite()
                }
            })
            this.nestNotificationDisposer = nestNotificationDisposer
            await this.tasks.scheduledTurns.initialize()
            if (!this.isGuest) {
                this.seedActivitySummaryTaskRecords(productState)
                await this.writeComputerActivitySummary().catch((error) => {
                    console.warn("[CodeStore] Failed to publish initial computer activity summary", error)
                })
            }
            this.installActivitySummaryProjection()
            void this.refreshRuntimeStateFromNest()

            this.initializeAnalytics(personalSettingsConnection.store)
            this.repos.startIdentityGeneration()
        } catch (err) {
            console.error("[CodeStore] Failed to initialize stores:", err)
            runInAction(() => {
                this.storeInitializing = false
            })
            throw err
        }
    }

    private installNestReconnectRefresh(): void {
        this.nestStatusDisposer?.()
        this.nestStatusDisposer = nestClient.onStatusChange((status) => {
            if (status !== "connected") return
            void this.refreshProviderList()
            void this.refreshRuntimeStateFromNest()
        })
    }

    private installActivitySummaryProjection(): void {
        this.activitySummaryReactionDisposer?.()
        this.activitySummaryReactionDisposer = reaction(
            () => `${this.productState?.attentionNow ?? ""}|${[...this.runtimes.runningTaskIds].sort().join("\0")}`,
            () => this.scheduleComputerActivitySummaryWrite()
        )
    }

    private seedActivitySummaryTaskRecords(productState: ProductStateStore): void {
        this.activitySummaryTaskRecords.clear()
        for (const task of productState.tasks.values()) {
            if (!task.closed) this.activitySummaryTaskRecords.set(task.id, task)
        }
    }

    private updateActivitySummaryTaskRecord(taskId: string): void {
        const task = this.productState?.tasks.get(taskId)
        if (!task || task.closed) {
            this.activitySummaryTaskRecords.delete(taskId)
        } else {
            this.activitySummaryTaskRecords.set(taskId, task)
        }
        this.scheduleComputerActivitySummaryWrite()
    }

    private scheduleComputerActivitySummaryWrite(): void {
        // The summary is node-only and belongs to the computer's owner.
        if (this.isGuest) return
        if (this.activitySummaryWriteTimer) clearTimeout(this.activitySummaryWriteTimer)
        this.activitySummaryWriteTimer = setTimeout(() => {
            this.activitySummaryWriteTimer = null
            this.activitySummaryWriteQueue = this.activitySummaryWriteQueue
                .then(() => this.writeComputerActivitySummary())
                .catch((error) => console.warn("[CodeStore] Failed to update computer activity summary", error))
        }, 250)
    }

    private computerActivitySummary() {
        const productState = this.productState
        if (!productState) return null
        return buildComputerActivitySummary({
            repos: [...productState.repos.values()],
            tasks: [...this.activitySummaryTaskRecords.values()],
            runningTaskIds: this.runtimes.runningTaskIds,
        })
    }

    private async writeComputerActivitySummary(): Promise<void> {
        const value = this.computerActivitySummary()
        if (!value) return
        await requestNestMethod("appData.set", {
            namespace: ACTIVITY_SUMMARY_NAMESPACE,
            key: ACTIVITY_SUMMARY_KEY,
            value,
        })
    }

    /** Whether this client is a guest on someone else's computer. */
    get isGuest(): boolean {
        return this.access.state === "ready" && !this.access.node
    }

    /** Whether this client may start turns and mutate task state on this computer. */
    get canOperate(): boolean {
        return this.access.state !== "ready" || this.access.node || this.access.level === "operate"
    }

    async refreshAccess(): Promise<void> {
        try {
            const principal = await requestNestMethod("auth.principal.read", {})
            const meta: Record<string, unknown> | null =
                typeof principal.meta === "object" && principal.meta !== null && !Array.isArray(principal.meta)
                    ? (principal.meta as Record<string, unknown>)
                    : null
            // The owner records the intended level in the principal's meta because
            // view and propose carry identical grants on the node.
            const summary = summarizeGrants(principal.grants, meta?.level === "propose")
            const next: NodeAccess = {
                state: "ready",
                node: summary.node,
                level: summary.level,
                workspaceIds: summary.workspaceIds,
                principalId: principal.principalId,
                kind: principal.kind,
                displayName: principal.displayName ?? "",
                paused: principal.paused === true,
                ...(summary.expiresAt ? { expiresAt: summary.expiresAt } : {}),
                ...(meta && typeof meta.glyph === "string" ? { glyph: meta.glyph } : {}),
                ...(meta && typeof meta.color === "string" ? { color: meta.color } : {}),
            }
            runInAction(() => {
                this.access = next
            })
        } catch (error) {
            console.warn("[CodeStore] Could not read this client's principal", error)
            runInAction(() => {
                this.access = NODE_ACCESS_UNKNOWN
            })
        }
    }

    private async refreshProviderList(): Promise<void> {
        if (this.providerListRefreshPromise) return this.providerListRefreshPromise
        this.providerListRefreshPromise = requestNestMethod("provider.list")
            .then((providerList) => {
                providerModelRegistry.replaceFromProviderList(providerList)
            })
            .catch((error) => {
                console.warn("[CodeStore] Failed to refresh provider list; keeping existing provider model registry", error)
            })
            .finally(() => {
                this.providerListRefreshPromise = null
            })
        return this.providerListRefreshPromise
    }

    private async subscribeToNestNotifications(): Promise<() => void> {
        const unsubscribers = [
            nestClient.on("session.turn.updated", (_, notification) => {
                this.queueNestNotification(notification)
            }),
        ]

        let releaseSubscriptions: (() => void) | null = null
        try {
            const client = await connectNestClient()
            releaseSubscriptions = await client.retainSubscriptions([{ method: "session.turn.updated" }])
        } catch (error) {
            for (const unsubscribe of unsubscribers) unsubscribe()
            throw error
        }

        return () => {
            for (const unsubscribe of unsubscribers) unsubscribe()
            releaseSubscriptions?.()
        }
    }

    private queueNestNotification(notification: NestNotification): void {
        this.runtimeRefreshQueue = this.runtimeRefreshQueue
            .then(() => this.handleNestNotification(notification))
            .catch((err) => {
                console.warn("[CodeStore] Failed to apply Nest notification:", err)
            })
    }

    private async handleNestNotification(notification: NestNotification): Promise<void> {
        if (notification.method === "session.turn.updated") {
            await this.handleNestTurnUpdated(notification.params)
            return
        }
    }

    private async handleNestTurnUpdated(params: unknown): Promise<void> {
        const update = parseNestTurnUpdate(params)
        if (!update) return

        const taskRecord = this.findTaskRecordForNestSession(update.sessionId)
        if (taskRecord) {
            const terminal = this.applyNestRuntimeTurnUpdate(taskRecord.taskId, update, update.receivedAt)
            if (terminal) {
                // Nest turn notifications do not carry WildLark command source; direct session-thread turns are generic follow-up work.
                this.execution.notifyAfterEvent(taskRecord.taskId, "do", terminal === "completed", update.turnId)
            }
            return
        }

        this.storePendingNestTurnUpdate(update)
        console.debug("[CodeStore] Deferred Nest turn update until task action or session record is registered", {
            turnId: update.turnId,
            sessionId: update.sessionId,
            status: update.status,
        })
    }

    private applyNestRuntimeTurnUpdate(taskId: string, update: NestTurnRuntimeUpdate, observedAt = Date.now()): TerminalActionStatus | null {
        const status = update.status
        if (!status) return null

        this.tasks.recordNestSessionActivity({
            taskId,
            sessionId: update.sessionId,
            activityAt: update.completedAt ?? update.startedAt ?? update.queuedAt ?? new Date(observedAt).toISOString(),
        })
        this.runtimes.applyTurnUpdate(taskId, {
            turnId: update.turnId,
            sessionId: update.sessionId,
            status,
            queuedAt: update.queuedAt,
            startedAt: update.startedAt,
            completedAt: update.completedAt,
            error: update.error,
        })
        this.queuedTurns.applyRuntimeTurnUpdate(taskId, {
            turnId: update.turnId,
            status,
            updatedAt: update.completedAt ?? update.startedAt ?? update.queuedAt,
        })

        const terminal = nestTerminalActionStatus(status)
        return terminal && !this.runtimes.isTaskRunning(taskId) ? terminal : null
    }

    refreshRuntimeStateFromNest(): Promise<void> {
        if (this.runtimeReconciliationPromise) return this.runtimeReconciliationPromise
        const refresh = this.runtimeRefreshQueue.then(() => this.reconcileRuntimeStateFromNest())
        this.runtimeRefreshQueue = refresh
        this.runtimeReconciliationPromise = refresh
        void refresh.finally(() => {
            if (this.runtimeReconciliationPromise === refresh) this.runtimeReconciliationPromise = null
        })
        return refresh
    }

    private async reconcileRuntimeStateFromNest(): Promise<void> {
        try {
            const report = await reconcileRuntimeState({
                listLiveTurns: () => requestNestMethod("session.turn.live.list"),
                findTaskIdForSession: (sessionId) => this.findTaskRecordForNestSession(sessionId)?.taskId ?? null,
                applyRuntime: (taskId, turn: Turn) => {
                    const update = turnRuntimeUpdateFromNestTurn(turn)
                    this.tasks.recordNestSessionActivity({
                        taskId,
                        sessionId: update.sessionId,
                        activityAt: update.completedAt ?? update.startedAt ?? update.queuedAt ?? new Date().toISOString(),
                    })
                    this.runtimes.applyLiveTurnUpdate(taskId, update)
                    this.queuedTurns.applyRuntimeTurnUpdate(taskId, {
                        turnId: update.turnId,
                        status: update.status,
                        updatedAt: update.completedAt ?? update.startedAt ?? update.queuedAt,
                    })
                },
                seedQueuedTurn: (taskId, turn) => {
                    this.queuedTurns.seedQueuedTurn(taskId, turn)
                },
                reconcileRuntimes: (liveTurnIds) => {
                    const removed = this.runtimes.reconcileLiveTurnIds(liveTurnIds)
                    const turnIdsByTask = new Map<string, string[]>()
                    for (const runtime of removed) {
                        this.queuedTurns.removeRuntimeTurn(runtime.taskId, runtime.turnId)
                        const turnIds = turnIdsByTask.get(runtime.taskId) ?? []
                        turnIds.push(runtime.turnId)
                        turnIdsByTask.set(runtime.taskId, turnIds)
                    }
                    for (const [taskId, turnIds] of turnIdsByTask) {
                        this.tasks.reconcileMissingRuntimeTurns(taskId, turnIds)
                    }
                    return removed.length
                },
            })
            recordMobileReconciliationSuccess()
            console.info("[CodeStore] Runtime reconciliation complete", report)
        } catch (error) {
            console.warn("[CodeStore] Runtime reconciliation failed; continuing with live notifications only", error)
        }
    }

    private findTaskRecordForNestSession(sessionId: string | undefined): { repoId: string; taskId: string } | null {
        const taskId = sessionId ? this.findTaskIdForNestSession(sessionId) : null
        const task = taskId ? this.productState?.tasks.get(taskId) : null
        return task ? { repoId: task.repoId, taskId: task.id } : null
    }

    findTaskIdForNestSession(sessionId: string): string | null {
        return this.taskOwnership.taskIdForSession(sessionId)
    }

    ownedNestSessionIds(): string[] {
        return this.taskOwnership.sessionIds()
    }

    private storePendingNestTurnUpdate(update: ParsedNestTurnUpdate): void {
        this.trimPendingNestTurnUpdates()
        const key = nestTurnUpdateKey(update.turnId, update.sessionId)
        const previous = this.pendingNestTurnUpdates.get(key)
        this.pendingNestTurnUpdates.set(
            key,
            previous
                ? {
                      ...previous,
                      ...update,
                      sessionId: update.sessionId ?? previous.sessionId,
                      queuedAt: update.queuedAt ?? previous.queuedAt,
                      startedAt: update.startedAt ?? previous.startedAt,
                      completedAt: update.completedAt ?? previous.completedAt,
                  }
                : update
        )

        if (this.pendingNestTurnUpdates.size <= PENDING_NEST_TURN_UPDATE_MAX) return

        const oldest = [...this.pendingNestTurnUpdates.entries()].sort((a, b) => a[1].receivedAt - b[1].receivedAt)[0]
        if (oldest) this.pendingNestTurnUpdates.delete(oldest[0])
    }

    private trimPendingNestTurnUpdates(now = Date.now()): void {
        for (const [key, update] of this.pendingNestTurnUpdates.entries()) {
            if (now - update.receivedAt <= PENDING_NEST_TURN_UPDATE_TTL_MS) continue

            this.pendingNestTurnUpdates.delete(key)
            console.warn("[CodeStore] Dropped stale unmatched Nest turn update", {
                turnId: update.turnId,
                sessionId: update.sessionId,
                status: update.status,
            })
        }
    }

    async applyPendingNestTurnUpdatesForTask(taskId: string): Promise<void> {
        this.trimPendingNestTurnUpdates()

        const updates = [...this.pendingNestTurnUpdates.values()].sort((a, b) => a.receivedAt - b.receivedAt)
        for (const update of updates) {
            const taskRecord = this.findTaskRecordForNestSession(update.sessionId)
            if (taskRecord?.taskId !== taskId) continue

            this.pendingNestTurnUpdates.delete(nestTurnUpdateKey(update.turnId, update.sessionId))
            const terminal = this.applyNestRuntimeTurnUpdate(taskId, update, update.receivedAt)
            if (terminal) {
                // Pending replay has only Nest session/turn data; direct session-thread turns are generic follow-up work.
                this.execution.notifyAfterEvent(taskId, "do", terminal === "completed", update.turnId)
            }
        }
    }

    private async initializeAnalytics(personalSettings: PersonalSettingsStore): Promise<void> {
        const settings = personalSettings.settings.get()
        const deviceConfig = await getDeviceConfig()
        const settingsDeviceId = settings?.deviceId?.trim() || null
        const localStorageDeviceId = getLocalAnalyticsDeviceIdBackup()
        const backupDeviceId = settingsDeviceId ?? localStorageDeviceId
        const backupSource: AnalyticsDeviceIdSource | null = settingsDeviceId
            ? "personal_settings_backup"
            : localStorageDeviceId
              ? "local_storage_backup"
              : null
        let deviceId: string
        let deviceIdSource: AnalyticsDeviceIdSource

        if (deviceConfig) {
            if (deviceConfig.wasGenerated && backupDeviceId && backupDeviceId !== deviceConfig.deviceId) {
                deviceId = backupDeviceId
                deviceIdSource = backupSource ?? "personal_settings_backup"
                const restoredConfig = await setDeviceConfigDeviceId(deviceId)
                if (!restoredConfig) {
                    console.warn("[Analytics] Failed to restore device ID from backup; using backup for renderer analytics")
                }
            } else {
                deviceId = deviceConfig.deviceId
                deviceIdSource = deviceConfig.wasGenerated ? "device_config_generated" : "device_config_existing"
            }
        } else {
            if (backupDeviceId) {
                deviceId = backupDeviceId
                deviceIdSource = backupSource === "local_storage_backup" ? "local_storage" : "personal_settings"
            } else {
                deviceId = crypto.randomUUID()
                deviceIdSource = "generated"
            }
        }

        if (settings?.deviceId !== deviceId) {
            personalSettings.settings.set({ deviceId })
        }
        setLocalAnalyticsDeviceIdBackup(deviceId)

        const telemetryDisabled = settings?.telemetryDisabled ?? false
        analytics.setEnabled(!telemetryDisabled)
        analytics.init(deviceId)
        track("app_opened", {
            deviceIdSource,
            deviceConfigWasGenerated: deviceConfig?.wasGenerated ?? false,
            deviceConfigReadFailed: deviceConfig?.readFailed ?? false,
        })

        this.telemetryReactionDisposer = reaction(
            () => this.personalSettingsStore?.settings.get()?.telemetryDisabled,
            (disabled) => {
                // Track before toggling so the event is sent while analytics is still active
                track(disabled ? "telemetry_disabled" : "telemetry_enabled")
                analytics.setEnabled(!disabled)
                setTelemetryDisabled(disabled ?? false)
            }
        )

        const PING_INTERVAL_MS = 12 * 60 * 60 * 1000
        this.pingIntervalId = setInterval(() => {
            track("ping")
        }, PING_INTERVAL_MS)

        // Track when the user returns to the app after being away for >30s
        const FOCUS_DEBOUNCE_MS = 30 * 1000
        let lastBlurTime = 0
        this.blurHandler = () => {
            lastBlurTime = Date.now()
        }
        this.focusHandler = () => {
            if (lastBlurTime > 0 && Date.now() - lastBlurTime > FOCUS_DEBOUNCE_MS) {
                track("app_focused")
            }
        }
        window.addEventListener("blur", this.blurHandler)
        window.addEventListener("focus", this.focusHandler)
    }

    async backfillTaskUsagePreview(repoId: string, taskId: string): Promise<void> {
        void repoId
        void taskId
    }

    async refreshRepoStoreFromStorage(): Promise<void> {
        await this.productState?.load()
    }

    async reloadRepoStoreFromStorage(): Promise<void> {
        await this.productState?.load()
    }

    disconnectAllStores(): void {
        this.repos.stopIdentityGeneration()
        this.pendingNestTurnUpdates.clear()
        this.proposals.dispose()

        if (this.nestNotificationDisposer) {
            this.nestNotificationDisposer()
            this.nestNotificationDisposer = null
        }
        if (this.nestStatusDisposer) {
            this.nestStatusDisposer()
            this.nestStatusDisposer = null
        }
        if (this.productStateRecordDisposer) {
            this.productStateRecordDisposer()
            this.productStateRecordDisposer = null
        }
        if (this.activitySummaryReactionDisposer) {
            this.activitySummaryReactionDisposer()
            this.activitySummaryReactionDisposer = null
        }
        if (this.activitySummaryWriteTimer) {
            clearTimeout(this.activitySummaryWriteTimer)
            this.activitySummaryWriteTimer = null
        }
        this.activitySummaryTaskRecords.clear()

        if (this.productState) {
            this.productState.dispose()
            this.productState = null
        }

        if (this.mcpServerStoreConnection) {
            this.mcpServerStoreConnection.disconnect()
            this.mcpServerStoreConnection = null
            this.mcpServerStore = null
        }

        if (this.personalSettingsStoreConnection) {
            this.personalSettingsStoreConnection.disconnect()
            this.personalSettingsStoreConnection = null
            this.personalSettingsStore = null
        }

        if (this.savedActions) {
            this.savedActions.dispose()
            this.savedActions = null
        }

        this.scratchpads.disconnectAll()
        this.timelines.dispose()
        this.accountUsage.reset()
        this.runtimes.clear()
        this.queuedTurns.clear()
        this.tasks.scheduledTurns.disconnect()
        this.taskOwnership.clear()

        if (this.telemetryReactionDisposer) {
            this.telemetryReactionDisposer()
            this.telemetryReactionDisposer = null
        }

        if (this.pingIntervalId) {
            clearInterval(this.pingIntervalId)
            this.pingIntervalId = null
        }

        if (this.blurHandler) {
            window.removeEventListener("blur", this.blurHandler)
            this.blurHandler = null
        }
        if (this.focusHandler) {
            window.removeEventListener("focus", this.focusHandler)
            this.focusHandler = null
        }

        this.mcpServers.dispose()
        this.storeInitialized = false
    }

    get isWorking(): boolean {
        return this.runtimes.hasRunningTasks
    }

    get currentUser(): User {
        return this.config.getCurrentUser()
    }

    get showTestFiles(): boolean {
        return this.personalSettingsStore?.settings.current.showTestFiles ?? false
    }

    setShowTestFiles(show: boolean): void {
        this.personalSettingsStore?.settings.set({ showTestFiles: show })
    }

    get gitDiffStrategy(): GitDiffStrategy {
        return this.personalSettingsStore?.settings.current.gitDiffStrategy ?? "current"
    }

    setGitDiffStrategy(strategy: GitDiffStrategy): void {
        this.personalSettingsStore?.settings.set({ gitDiffStrategy: strategy })
    }

    requireProductState(): ProductStateStore {
        if (!this.productState) throw new Error("ProductStateStore not initialized")
        return this.productState
    }

    isTaskRunning(taskId: string): boolean {
        return this.runtimes.isTaskRunning(taskId)
    }

    private persistNewTaskAgentDefaults(): void {
        this.personalSettingsStore?.settings.set({
            newTaskHarnessId: this.defaultHarnessId,
            newTaskModelId: this.defaultModel,
            newTaskThinking: this.defaultThinking,
        })
    }

    setDefaultModel(modelId: string): void {
        this.defaultModel = modelId
        this.persistNewTaskAgentDefaults()
    }

    setDefaultThinking(level: ThinkingLevel): void {
        this.defaultThinking = level
        this.persistNewTaskAgentDefaults()
    }

    setDefaultFastMode(enabled: boolean): void {
        this.defaultFastMode = enabled
    }

    recordNewTaskAgentLaunch(target: NewTaskAgentTarget): void {
        this.defaultHarnessId = target.harnessId
        this.defaultModel = target.modelId
        if (target.thinking) this.defaultThinking = target.thinking
        const settings = this.personalSettingsStore?.settings
        if (!settings) return
        settings.set({
            newTaskHarnessId: target.harnessId,
            newTaskModelId: target.modelId,
            newTaskThinking: this.defaultThinking,
        })
    }

    setNewTaskPinnedAgents(targets: readonly NewTaskAgentTarget[]): void {
        this.personalSettingsStore?.settings.set({
            newTaskPinnedAgents: normalizeNewTaskPinnedAgents(targets),
        })
    }

    setDefaultHarnessId(harnessId: HarnessId): void {
        this.defaultHarnessId = harnessId
        // Reset default model to match the new harness
        this.defaultModel = getDefaultModelForHarness(harnessId)
        this.persistNewTaskAgentDefaults()
    }
}
